/**
 * Outstanding approvals, across the whole desk — Step 47.
 *
 * Kai asked for "some management" alongside the engine. The question a
 * service-desk manager actually has is *what is stuck, and on whom* — which
 * no per-ticket panel can answer, because the stuck ones are by definition
 * the tickets nobody is looking at.
 *
 * Read-mostly: it lists and it cancels. It does not let an admin APPROVE on
 * somebody else's behalf, and that omission is deliberate — an approval is a
 * person saying yes, and a button that forges one would make the whole audit
 * trail worthless. Cover for a real absence is what delegation is for.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { fullTime, relativeTime } from '../../lib/time';
import { ticketRef, APPROVAL_MODE_SHORT, type ApprovalMode } from '@itsm/shared';
import { ticketPath } from '../../features/tickets/api';
import { useConfirm } from '../../lib/confirm';

interface Row {
  requestId: string;
  chainId: string;
  stage: number;
  mode: string;
  note: string | null;
  requestedAt: number;
  remindedAt: number | null;
  entityId: string;
  ticketNumber: number | null;
  ticketSubject: string | null;
  ticketType: string | null;
  waitingOn: string[];
}

export function ApprovalsAdminPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: qk.approvals.manage,
    queryFn: () => apiFetch<{ entries: Row[] }>('/api/approvals/manage'),
  });

  const cancel = useMutation({
    mutationFn: (chainId: string) => apiSend(`/api/approvals/chain/${chainId}/cancel`, 'POST'),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not cancel that.');
      void qc.invalidateQueries({ queryKey: qk.approvals.manage });
    },
  });

  const entries = q.data?.entries ?? [];

  return (
    <div className="max-w-4xl space-y-4 p-6">
      <div>
        <h1 className="text-lg font-semibold">Outstanding approvals</h1>
        <p className="text-sm text-muted-foreground">Waiting for a decision, oldest first.</p>
      </div>

      {q.isError && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-destructive">Could not load approvals</p>
            <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}
      {q.isPending && (
        <Card>
          <CardContent className="space-y-3 p-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      )}
      {q.data && entries.length === 0 && (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            Nothing is waiting for approval.
          </CardContent>
        </Card>
      )}

      {entries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            {entries.map((e) => (
              <div key={e.requestId} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0">
                <button
                  onClick={() => navigate(ticketPath(e.ticketType ?? 'incident', e.entityId, 'approvals'))}
                  className="min-w-0 flex-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="block truncate text-sm">
                    <span className="mr-2 tabular-nums text-muted-foreground">
                      {e.ticketNumber ? ticketRef(e.ticketType ?? 'service_request', e.ticketNumber) : '—'}
                    </span>
                    {e.ticketSubject ?? e.entityId}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    Stage {e.stage} · {APPROVAL_MODE_SHORT[e.mode as ApprovalMode]} · waiting on{' '}
                    {e.waitingOn.join(', ') || 'nobody — this is stuck'}
                  </span>
                </button>
                {/* Age is the whole point of this page, so it is the one
                    number given prominence. */}
                <Badge variant="outline" title={fullTime(e.requestedAt)}>
                  {relativeTime(e.requestedAt)}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: 'Cancel this approval?',
                        body: 'The requester will need a new one.',
                        destructive: true,
                        confirmLabel: 'Cancel it',
                        cancelLabel: 'Leave it',
                      })
                    )
                      cancel.mutate(e.chainId);
                  }}
                >
                  Cancel
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
